import { jwtDecode } from "jwt-decode"
import { getDoctorsList, getUser, getSharedUser } from "../services/http-service/api"

export const ROLE_ADMIN = 0
export const ROLE_USER = 1
export const ROLE_DOCTOR = 2
export const ROLE_MANAGER = 3
export const ROLE_ENG = 4
export const ROLES = [ROLE_ADMIN, ROLE_USER, ROLE_DOCTOR, ROLE_MANAGER, ROLE_ENG]
export const ROLE_VALUES = ['ADMIN', 'USER', 'DOCTOR', 'MANAGER', 'ENG']

export const getRoleByValue = (value) => {
  const index = ROLE_VALUES.indexOf(value)
  return index !== -1 ? ROLES[index] : null
}

export const getGender = (patient) => {
  return patient &&
    patient.profile &&
    (/^male$/i).test(patient.profile.data.gender)
    ? "Мужской"
    : patient &&
      patient.profile &&
      (/^female$/i).test(patient.profile.data.gender) ? "Женский" : ""
}

const fetchDoctorsList = async () => {
  const doctorsResponse = await getDoctorsList()
  if (doctorsResponse.status === 200) {
    return doctorsResponse.data
  }
  if (doctorsResponse.status === 404) {
    return null // invalid
  }
  return undefined // other errors, continue
}

const fetchRoles = async (user) => {
  let doctorsList = null

  // Process ROLE_DOCTOR
  if (user.roles.includes(ROLE_DOCTOR)) {
    doctorsList = await fetchDoctorsList()
    if (doctorsList === null) return null // invalid token
    if (doctorsList) {
      const doctor = doctorsList.find(d => d.userId === user.uid)
      user.doctor = doctor
    }
  }

  // Process ROLE_USER
  if (user.roles.includes(ROLE_USER)) {
    const userResponse = await getUser(user.uid)
    if (userResponse && userResponse.data) {
      user.user = userResponse.data

      const doctorId = +userResponse.data.profile?.data?.['doctor']
      if (doctorId) {
        if (!doctorsList) {
          doctorsList = await fetchDoctorsList()
          if (doctorsList === null) return null // invalid token
        }
        if (doctorsList) {
          const doctor = doctorsList.find(d => d.id === doctorId)
          if (!user.doctor) {
            user.doctor = doctor
          }
        }
      }
    }
  }

  return user
}

export const getUserFromToken = async (storedToken) => {
  // If no bearer token is provided, try share token from URL
  if (!storedToken && typeof window !== 'undefined') {
    const shareToken = new URLSearchParams(window.location.search).get('share')
    if (shareToken) {
      const sharedResponse = await getSharedUser(shareToken)
      if (sharedResponse.status === 200 && sharedResponse.data) {
        const user = sharedResponse.data
        const roles = user.roles.map(getRoleByValue)
        const visitor = {
          ...user,
          id: user.userId,
          uid: user.userId,
          token: shareToken,
          roles,
          isAdmin: roles.includes(ROLE_ADMIN) || false,
          shared: true,
        }
        return await fetchRoles(visitor)
      }
      return null
    }
  }

  const tokenData = storedToken && storedToken.split(" ", 2)
  if (tokenData && tokenData.length == 2) {
    try {
      const token = jwtDecode(tokenData[1])
      const roles = token.roles || [token.role]

      const visitor = {
        ...token,
        roles,
        token: tokenData[1],
        id: token.uid,
        uid: token.uid,
        isAdmin: roles.includes(ROLE_ADMIN)
      }

      return await fetchRoles(visitor)
    } catch (error) {
      console.error("Error decoding token or fetching user data:", error)
      return null
    }
  }
  return null
}

export const isShareTokenAccess = (visitor) => {
  return !!(visitor && visitor.shared)
}

export const patientsAreAnonymous = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['patients_are_anonymous']
}

/**
 * @typedef {object} PatientIdentitySettings
 * @property {boolean} anonymous
 * @property {string | undefined} identityType
 */

/**
 * @param {{groups?: Array<{profile?: import('../types/profile').Profile<import('../types/profile').DoctorGroupProfileData>}>}} doctor
 * @returns {PatientIdentitySettings} Настройки отображения идентификатора пациента.
 */
export const getPatientIdentitySettings = (doctor) => {
  const doctorProfileData = /** @type {import('../types/profile').DoctorGroupProfileData} */ (
    doctor?.groups?.[0]?.profile?.data || {}
  )
  return {
    anonymous: doctorProfileData.patients_are_anonymous === true,
    identityType: doctorProfileData.patients_identity_type,
  }
}

export const patientsAreShared = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['patients_are_shared']
}

export const aiAllowed = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['ai_allowed']
}

export const visitsEnabled = (visitor) => {
  return visitor && visitor.doctor && visitor.doctor.groups && visitor.doctor.groups.length > 0 && visitor.doctor.groups[0].profile.data['visits_enabled']
}

export const examStatus = (exam) => {
  let status = exam.profile?.data?.external_id ? "DAMUMED" : exam.status
  switch (status) {
    case 'OPEN':
      return exam.byDoctor ? "Назначено" : "Направлено доктору"
    case 'DAMUMED':
      return 'Направлено в Damumed'
    case 'CLOSED':
      return 'Получен'
    case 'ACTIVE':
      return 'Выполняется'
    case 'INACTIVE':
      return 'Пройдено'
    case 'UPLOADING':
      return 'В процессе выгрузки'
  }
}

export const aiStatus = (point) => {
  const pointData = JSON.parse(point && point.media?.profile?.data?.external_data2 || "{}")
  console.log('Point', pointData)
  const result = pointData.results?.find(result => result.filename == point.media.fileName)
  console.log('Result', result)
  return `AI-заключение: ${result ? 'получено' : 'не получено'}`
  /*
  const name = pointData.data && pointData.data.length > 0 && pointData.data[0].result.aiResult.probParams.class
      && pointData.data[0].result.aiResult.probParams.class.length > 0 ?
      pointData.data[0].result.aiResult.probParams.class[0].name : ""
  if (name === 'bad_endo') {
      return "AI заключение: не является эндоскопией уха"
  }
  if (name === 'nose') {
    return 'AI заключение: Риноскопия'
  }
  if (name === 'normal') {
    return 'AI заключение: 🟢 Нет патологии'
  }
  if (name === 'pathology') {
    return 'AI заключение: 🔴 Патология'
  }
  return `AI-заключение: ${name || 'не получено'}`
  */
}

const filterAIResults = (examTypeId, total, tags) => {
  if (total === 1) {
    return true
  }
  switch (examTypeId) {
    case 3:
      return tags.includes('Ухо')
    case 4:
      return tags.includes('Нос')
    default:
      return false
  }
}

export const parseAIResponse = (examTypeId, media) => {
  const fps = media.profile.data['frames'] / media.profile.data['length']
  const data = media.profile.data['external_data2'] && JSON.parse(media.profile.data['external_data2'])
  const info = data && data.results && data.results.length > 0 && data.results[0].results_info
  const status = info && info.results ? info.results
    .filter(r => filterAIResults(examTypeId, info.results.length, r.cascade ? Object.values(r.cascade) : []))
    .map(result => {
      const model = {
        model: `${result.model}`,
        tags: result.cascade ? Object.values(result.cascade).join(',') : '',
        status: []
      }
      result.prediction_classes.map(prediction => {
        const start_time = Math.round(prediction.video_segments.start_frame / fps * 100) / 100
        const end_time = Math.round(prediction.video_segments.end_frame / fps * 100) / 100
        model.status.push({
          name: prediction.name,
          confidence: prediction.confidence,
          start: start_time,
          end: end_time
        })
      })
      return model
    }) : []
  return status
}
